import type { PointerEvent as ReactPointerEvent, RefObject } from 'react';
import type { Guide } from '../../core/selection';
import { useEditorStore } from '../../state/editorStore';
import { clientToStage, stageToDoc } from '../pointer';

/**
 * Guide dragging, shared by the rulers (which create guides) and the stage
 * (which moves them).
 *
 * Passing `index: null` creates a guide at the pointer first, so a single
 * press-drag from a ruler drops a guide exactly where it is released.
 */
export function useGuideDrag(stageRef: RefObject<HTMLDivElement | null>) {
  return (axis: Guide['axis'], index: number | null) => (event: ReactPointerEvent) => {
    const stage = stageRef.current;
    if (!stage || event.button !== 0) return;
    event.preventDefault();
    event.stopPropagation();

    const store = useEditorStore.getState();
    const positionOf = (clientX: number, clientY: number) => {
      const point = stageToDoc(clientToStage(clientX, clientY, stage), store.viewport);
      return Math.round(axis === 'x' ? point.x : point.y);
    };

    let target = index;
    if (target === null) {
      // The new guide lands at the end of the list, so its index is the old length.
      target = useEditorStore.getState().guides.length;
      store.addGuide({ axis, position: positionOf(event.clientX, event.clientY) });
    }
    const indexToMove = target;

    const onMove = (moveEvent: PointerEvent) => {
      useEditorStore.getState().moveGuide(indexToMove, {
        axis,
        position: positionOf(moveEvent.clientX, moveEvent.clientY),
      });
    };
    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };

    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };
}
